import {
  LoadingOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Alert, Button, Space } from 'antd'
import { useRuntimeVariables } from 'app-kit'
import { AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import { LibraDocumentContext } from 'features/libra/document/LibraDocumentContext'
import { useDocumentProcessingReady } from 'features/libra/document/useDocumentProcessingReady'
import {
  stampArrayRowIds,
  stripArrayRowIds,
} from 'features/libra/fieldValues/arrayRowIds'
import { shouldPreserveValueOnPrefill } from 'features/libra/fieldValues/shouldPreserveValueOnPrefill'
import {
  isEmptyFieldValue,
  withFieldOrigin,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValue,
  FieldValues,
  FieldValuesSchema,
} from 'features/libra/ontology/ontologyFieldTypes'
import { AiPrefillButton } from 'features/libra/stepComponents/AiPrefillButton'
import {
  AiPrefillGate,
  prefillLabel,
} from 'features/libra/stepComponents/AiPrefillControls'
import { usePrefillBlockedReason } from 'features/libra/stepComponents/prefillGate'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useReprocessDocuments } from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import type { FC } from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import cantileverTheme from 'styles/cantileverTheme'
import { RolodexPermissionsValues } from 'types'

import {
  type AiPrefillDisagreement,
  AiPrefillDisagreements,
} from './AiPrefillDisagreements'

// Keep existing callers on the banner import path.
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { AiCitationChip, extractLlmCitation } from './AiCitationChip'

/** Identifies a field the AI attempted but could not extract from documents. */
export function isAiUnextracted(fieldValue: FieldValue | undefined): boolean {
  return fieldValue?.aiExtracted === false
}

type PrefillResponseSnapshot = {
  ticketId: string
  sectionId: string
  fieldValues: FieldValues
}

export function derivePrefillDisagreements(
  snapshot: PrefillResponseSnapshot | null,
  ticketId: string,
  sectionId: string,
  fieldValues: FieldValues,
  fieldLabels: Record<string, string>
): ReadonlyArray<AiPrefillDisagreement> {
  if (
    !snapshot ||
    snapshot.ticketId !== ticketId ||
    snapshot.sectionId !== sectionId
  ) {
    return []
  }

  return Object.entries(snapshot.fieldValues).flatMap(
    ([fieldId, suggestion]) => {
      if (
        !Object.hasOwn(fieldLabels, fieldId) ||
        isEmptyFieldValue(suggestion.value)
      ) {
        return []
      }

      const current = fieldValues[fieldId]
      if (
        !current ||
        !shouldPreserveValueOnPrefill(current) ||
        isEqual(
          stripArrayRowIds(current.value),
          stripArrayRowIds(suggestion.value)
        )
      ) {
        return []
      }

      return [
        {
          fieldId,
          fieldLabel: fieldLabels[fieldId],
          current,
          suggestion,
        },
      ]
    }
  )
}

export const AiAutofillAlert = styled(Alert)`
  min-height: 48px;

  .ant-alert-icon {
    color: ${cantileverTheme.colors.OceanBlue} !important;
  }
`

/**
 * Assesses one agent section and applies the shared provenance and row-ID rules
 * used by both single-section and multi-section pre-fill actions. Every
 * returned wrapper, including a not-found one, is marked `llm`.
 */
export async function postAssessSection(
  axios: AuthedAxiosRequests,
  gongAgentUrl: string,
  {
    ticketId,
    sectionId,
    inputs,
  }: {
    ticketId: string
    sectionId: string
    inputs?: Record<string, unknown>
  }
): Promise<{ fieldValues: FieldValues; documentsUsed: string[] }> {
  const response = await axios.post(`${gongAgentUrl}/api/v1/assess/section`, {
    ticket_id: ticketId,
    section_id: sectionId,
    ...(inputs && { inputs }),
  })
  const stamped = stampArrayRowIds(
    FieldValuesSchema.parse(response.data?.field_values ?? {})
  )
  const documentsUsed: string[] = response.data?.documents_used ?? []
  const fieldValues = Object.fromEntries(
    Object.entries(stamped).map(([k, fv]) => [
      k,
      withFieldOrigin(
        fv.value === null || fv.value === undefined
          ? { ...fv, aiExtracted: false, documentsSearched: documentsUsed }
          : fv,
        'llm'
      ),
    ])
  )
  return { fieldValues, documentsUsed }
}

/**
 * Narrows an unknown thrown value to the axios-style error envelope we read
 * (HTTP status + server `detail` message) without depending on axios types.
 */
function getAxiosError(err: unknown): { status?: number; detail?: string } {
  const e = err as {
    response?: { status?: number; data?: { detail?: string } }
  }
  return { status: e?.response?.status, detail: e?.response?.data?.detail }
}

/** Runs an agent section assessment; callers must pass stable, memoized inputs. */
export function useGongAgentAutofill({
  sectionId,
  onFieldValues,
  inputs,
}: {
  sectionId: string
  onFieldValues: (
    fieldValues: FieldValues,
    meta: { documentsUsed: string[] }
  ) => void
  inputs?: Record<string, unknown>
}) {
  const { GONG_AGENT_URL } = useRuntimeVariables()
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ticketId } = useTicketAccess()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [documentsUsed, setDocumentsUsed] = useState<string[]>([])
  const [extractedAny, setExtractedAny] = useState<boolean | null>(null)

  const blockedReason = usePrefillBlockedReason()

  const autofill = useCallback(async () => {
    if (blockedReason) return
    if (!GONG_AGENT_URL) {
      setError('GONG_AGENT_URL is not configured')
      return
    }
    setLoading(true)
    setError(null)
    setDocumentsUsed([])
    setExtractedAny(null)
    try {
      const { fieldValues: markedFieldValues, documentsUsed } =
        await postAssessSection(axios, GONG_AGENT_URL, {
          ticketId,
          sectionId,
          inputs,
        })
      onFieldValues(markedFieldValues, { documentsUsed })
      setDocumentsUsed(documentsUsed)
      setExtractedAny(
        Object.values(markedFieldValues).some(
          (fv) =>
            fv.aiExtracted !== false &&
            !(Array.isArray(fv.value) && fv.value.length === 0)
        )
      )
    } catch (err: unknown) {
      setError(getAxiosError(err).detail ?? 'AI pre-fill failed')
    } finally {
      setLoading(false)
    }
  }, [
    blockedReason,
    GONG_AGENT_URL,
    axios,
    ticketId,
    sectionId,
    onFieldValues,
    inputs,
  ])

  return {
    autofill,
    loading,
    error,
    documentsUsed,
    extractedAny,
  }
}

export const AiAutofillBanner: FC<{
  section: { id: string; agentPermissions?: string[] }
  fieldValues: FieldValues
  fieldLabels: Record<string, string>
  onFieldValues: (fieldValues: FieldValues) => void
}> = ({ section, fieldValues, fieldLabels, onFieldValues }) => {
  const { ticketId } = useTicketAccess()
  const documentContext = useContext(LibraDocumentContext)
  const [responseSnapshot, setResponseSnapshot] =
    useState<PrefillResponseSnapshot | null>(null)
  const handleFieldValues = useCallback(
    (response: FieldValues) => {
      onFieldValues(response)
      setResponseSnapshot({
        ticketId,
        sectionId: section.id,
        fieldValues: response,
      })
    },
    [onFieldValues, ticketId, section.id]
  )
  const { autofill, loading, error, documentsUsed, extractedAny } =
    useGongAgentAutofill({
      sectionId: section.id,
      onFieldValues: handleFieldValues,
    })
  const {
    allProcessed,
    isCheckingProcessing,
    unprocessedCount,
    failedEntryIds,
    refreshProcessingStatus,
  } = useDocumentProcessingReady()
  const reprocessDocuments = useReprocessDocuments()

  const [reprocessing, setReprocessing] = useState(false)
  const [reprocessError, setReprocessError] = useState<string | null>(null)

  const docsNotReady = !allProcessed && !isCheckingProcessing
  const hasFailures = failedEntryIds.length > 0

  const onReprocess = useCallback(async () => {
    setReprocessing(true)
    setReprocessError(null)
    try {
      await reprocessDocuments(failedEntryIds)
    } catch (err: unknown) {
      const { status, detail } = getAxiosError(err)
      setReprocessError(
        status === 403
          ? 'You need Rolodex admin access to reprocess documents.'
          : (detail ?? 'Failed to reprocess documents')
      )
      setReprocessing(false)
      return
    }
    // Re-query once without polling; the user refreshes to watch reprocessing.
    refreshProcessingStatus()
  }, [reprocessDocuments, failedEntryIds, refreshProcessingStatus])

  // Derive type and description together so the banner state cannot drift.
  const { alertType, description } = useMemo<{
    alertType: 'success' | 'info' | 'warning' | 'error'
    description?: string
  }>(() => {
    if (reprocessError)
      return { alertType: 'error', description: reprocessError }
    if (reprocessing)
      return {
        alertType: 'info',
        description:
          'Reprocessing started. Documents are being re-queued; refresh to check progress.',
      }
    if (error) return { alertType: 'error', description: error }
    if (documentsUsed.length > 0)
      return extractedAny
        ? { alertType: 'success', description: undefined }
        : {
            alertType: 'warning',
            description:
              'Documents were found but no data could be extracted. Fields need to be filled manually.',
          }
    if (extractedAny === false)
      return {
        alertType: 'warning',
        description:
          'No relevant documents were found. Please ensure Documents are uploaded and processed.',
      }
    if (hasFailures)
      return {
        alertType: 'warning',
        description: `${failedEntryIds.length} document${failedEntryIds.length === 1 ? '' : 's'} failed processing. Reprocess to try again.`,
      }
    if (docsNotReady)
      return {
        alertType: 'warning',
        description: `${unprocessedCount} document${unprocessedCount === 1 ? '' : 's'} still processing. Pre-fill with AI will be available once all documents are processed.`,
      }
    return { alertType: 'info', description: undefined }
  }, [
    reprocessError,
    reprocessing,
    error,
    documentsUsed,
    extractedAny,
    hasFailures,
    failedEntryIds,
    docsNotReady,
    unprocessedCount,
  ])

  const buttonDisabled = docsNotReady || isCheckingProcessing
  const onPrefill = useCallback(() => {
    setResponseSnapshot(null)
    void autofill()
  }, [autofill])
  const disagreements = derivePrefillDisagreements(
    responseSnapshot,
    ticketId,
    section.id,
    fieldValues,
    fieldLabels
  )

  return (
    <AiPrefillGate
      permissions={
        (section.agentPermissions as RolodexPermissionsValues[]) ?? []
      }>
      <>
        <AiAutofillAlert
          icon={
            loading || reprocessing ? (
              <LoadingOutlined spin />
            ) : (
              <ThunderboltOutlined />
            )
          }
          showIcon
          type={alertType}
          title={prefillLabel({ loading: false, hasRun: false })}
          description={description}
          action={
            !loading && (
              <Space>
                {hasFailures && (
                  <Button
                    size='small'
                    icon={<ReloadOutlined />}
                    onClick={onReprocess}
                    loading={reprocessing}
                    disabled={reprocessing}>
                    Reprocess failed
                  </Button>
                )}
                <AiPrefillButton
                  type='primary'
                  size='small'
                  icon={<ThunderboltOutlined />}
                  onClick={onPrefill}
                  disabled={buttonDisabled}
                  tooltip={
                    buttonDisabled
                      ? 'Documents are still being processed'
                      : undefined
                  }>
                  {prefillLabel({
                    loading,
                    hasRun: extractedAny !== null,
                  })}
                </AiPrefillButton>
              </Space>
            )
          }
        />
        {disagreements.length > 0 && (
          <AiPrefillDisagreements
            disagreements={disagreements}
            hasDocumentProvider={documentContext !== undefined}
          />
        )}
      </>
    </AiPrefillGate>
  )
}
